<script setup lang="ts">
  import { Icon, IconName, IconSize, Input, Modal, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { SWITCH_CITY_TEXTS } from '../config/texts';
  import type { GroupedCities } from '../lib/groupCities';
  import type { SwitchCity } from '../model/types';

  interface SwitchCityModalProps {
    visible: boolean;
    grouped: GroupedCities;
    query: string;
    selectedId?: number | null;
    hasResults: boolean;
    mobile?: boolean;
  }

  withDefaults(defineProps<SwitchCityModalProps>(), {
    selectedId: null,
    mobile: false,
  });

  interface SwitchCityModalEmits {
    (e: 'update:query', value: string): void;
    (e: 'select', city: SwitchCity): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<SwitchCityModalEmits>();

  const MODAL_WIDTH = '580px';
</script>

<template>
  <Modal
    v-if="visible"
    :mobile="mobile"
    :size="mobile ? 'auto' : MODAL_WIDTH"
    :title="mobile ? '' : SWITCH_CITY_TEXTS.modalTitle"
    :closable="true"
    :z-index="10000"
    :overlay-closable="true"
    @close="emit('close')"
  >
    <div
      class="switch-city-modal"
      :class="{ 'switch-city-modal_mobile': mobile }"
      data-testid="switch-city-modal"
    >
      <div class="switch-city-modal__header">
        <Typo
          v-if="mobile"
          class="switch-city-modal__title"
          :variant="TypoVariant.Heading5"
          tag="p"
          data-testid="switch-city-modal-title"
        >
          {{ SWITCH_CITY_TEXTS.modalTitle }}
        </Typo>
        <Typo
          class="switch-city-modal__subtitle"
          :variant="TypoVariant.BodyXLRegular"
          tag="p"
        >
          {{ SWITCH_CITY_TEXTS.modalSubtitle }}
        </Typo>

        <div class="switch-city-modal__search">
          <Input
            :model-value="query"
            :placeholder="SWITCH_CITY_TEXTS.searchPlaceholder"
            size="l"
            data-testid="switch-city-search"
            @update:modelValue="emit('update:query', $event)"
          >
            <template #append>
              <Icon
                :name="IconName.Search"
                :size="IconSize.S"
              />
            </template>
          </Input>
        </div>
      </div>

      <div
        v-if="!hasResults"
        class="switch-city-modal__empty"
        data-testid="switch-city-nothing-found"
      >
        <Typo
          :variant="TypoVariant.BodyLRegular"
          tag="span"
        >
          {{ SWITCH_CITY_TEXTS.nothingFound }}
        </Typo>
      </div>

      <div
        v-else
        class="switch-city-modal__list"
      >
        <div class="switch-city-modal__group-container">
          <button
            v-for="city in grouped.pinned"
            :key="`pinned-${city.id}`"
            type="button"
            class="switch-city-modal__option"
            :class="{ 'switch-city-modal__option_selected': selectedId === city.id }"
            data-testid="switch-city-option"
            @click="emit('select', city)"
          >
            <Typo
              :variant="TypoVariant.BodyLRegular"
              tag="span"
            >
              {{ city.name }}
            </Typo>
          </button>
        </div>

        <div
          v-for="group in grouped.groups"
          :key="`group-${group.letter}`"
          class="switch-city-modal__group-container"
        >
          <div
            v-for="(city, index) in group.cities"
            :key="`city-${city.id}`"
          >
            <button
              type="button"
              class="switch-city-modal__option"
              :class="{ 'switch-city-modal__option_selected': selectedId === city.id }"
              data-testid="switch-city-option"
              @click="emit('select', city)"
            >
              <Typo
                :variant="TypoVariant.BodyLRegular"
                tag="span"
              >
                {{ city.name }}
              </Typo>
            </button>
            <div
              v-if="index === 0"
              class="switch-city-modal__group"
              data-testid="switch-city-group"
            >
              <Typo
                class="switch-city-modal__group-letter"
                variant="body-xl-700"
                tag="span"
              >
                {{ group.letter }}
              </Typo>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
  .switch-city-modal {
    $root: &;

    display: flex;
    flex-direction: column;
    min-height: 0;

    &__title {
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-m);
      text-align: center;
    }

    &__subtitle {
      color: var(--color-text-primary);
      margin: 0 0 var(--spacing-m);
      max-width: 320px;
    }

    &_mobile {
      #{$root}__header {
        background: var(--color-bg-primary);
        margin: 0 calc(-1 * var(--spacing-l));
        padding: 0 var(--spacing-l) var(--spacing-xs);
        position: sticky;
        top: 0;
        z-index: 1;
      }

      #{$root}__subtitle {
        max-width: none;
        text-align: center;
      }

      #{$root}__list {
        max-height: none;
      }
    }

    &__empty {
      color: var(--color-text-secondary);
      padding: var(--spacing-l) var(--spacing-m);
      text-align: center;
    }

    &__list {
      display: flex;
      flex-direction: column;
      max-height: 360px;
      overflow-y: auto;
      padding-left: 36px;
    }

    &__group {
      position: relative;
    }

    &__group-container {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      margin-top: var(--spacing-xl);
    }

    &__group-letter {
      color: var(--color-text-primary);
      left: calc(-1 * var(--spacing-xxl));
      position: absolute;
      top: calc(-1 * var(--spacing-l));
    }
  }

  .switch-city-modal__option {
    align-items: center;
    background: none;
    border: none;
    color: var(--color-text-primary);
    cursor: pointer;
    display: flex;
    opacity: 0.7;
    padding: 0;
    text-align: left;
    width: 100%;

    &_selected {
      color: var(--color-link);
      opacity: 1;
    }
  }
</style>
